import classNames from "classnames";
import { DropFile } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { Button } from "components/DesignSystem/Button/Button";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadIconV2 } from "components/icons/DownloadIconV2";
import { Preview } from "components/PreviewModal";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useToast } from "hooks/useToast";
import { useFormikContext } from "formik";
import { useState } from "react";
import {
  useDeleteChatQNADocumentMutation,
  useUploadChatQNADocumentMutation,
} from "store/apis/chatQnA";

type DocumentUploadCellProps = {
  answerRequestId: string;
  groupId: string;
  isDisabled: boolean;
  isSubmitted: boolean;
  allowMultiple?: boolean;
  onUploadSuccess?: () => void;
};

export const DocumentUploadCell = ({
  answerRequestId,
  groupId,
  isDisabled,
  isSubmitted,
  allowMultiple = true,
  onUploadSuccess,
}: DocumentUploadCellProps) => {
  const [uploadDocument, { isLoading: isUploading }] =
    useUploadChatQNADocumentMutation();
  const [deleteDocument] = useDeleteChatQNADocumentMutation();
  const { downloadFile } = useDownloadFile();
  const { promise } = useToast();
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [previewFileUuid, setPreviewFileUuid] = useState<string | null>(null);

  const fieldName = `documents_${answerRequestId}`;
  const { values, setFieldValue } =
    useFormikContext<Record<string, (DropFile & { file_uuid: string })[]>>();
  const uploadedFiles: (DropFile & { file_uuid: string })[] =
    values[fieldName] ?? [];

  const appendUploadedDoc = (
    result: Awaited<ReturnType<typeof uploadDocument>>
  ) => {
    if ("error" in result) return;

    const serverDocs =
      result.data?.requested_answers.find(
        ({ uuid }) => uuid === answerRequestId
      )?.documents ?? [];

    const existingUuids = new Set(uploadedFiles.map((f) => f.uuid));
    const uploadedDoc = serverDocs.find((doc) => !existingUuids.has(doc.uuid));

    if (uploadedDoc) {
      setFieldValue(fieldName, [
        ...uploadedFiles,
        {
          uuid: uploadedDoc.uuid,
          name: uploadedDoc.name,
          file_type: uploadedDoc.file_type as DropFile["file_type"],
          is_previewable: uploadedDoc.is_previewable,
          file_uuid: uploadedDoc.file_uuid,
        },
      ]);
      onUploadSuccess?.();
    }
  };

  const handleDelete = async (
    _: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    const file = uploadedFiles[index];
    setDeletingUuid(file.uuid);

    const mutation = deleteDocument({ groupId, documentId: file.uuid });

    promise(mutation.unwrap(), {
      loading: "Deleting document...",
      success: "Document deleted successfully.",
      error: "Failed to delete document. Please try again.",
    });

    const result = await mutation;
    if (!("error" in result)) {
      setFieldValue(
        fieldName,
        uploadedFiles.filter((_, i) => i !== index)
      );
    }
    setDeletingUuid(null);
  };

  const handleDrop = async (files: FileType[]) => {
    for (const file of files) {
      const payload =
        file instanceof File
          ? { groupId, requestedAnswerId: answerRequestId, file }
          : { groupId, requestedAnswerId: answerRequestId, fileId: file.uuid };
      const mutation = uploadDocument(payload);

      promise(mutation.unwrap(), {
        loading: "Uploading document...",
        success: "Document uploaded successfully.",
        error: "Failed to upload document. Please try again.",
      });

      const result = await mutation;
      appendUploadedDoc(result);
    }
  };

  if (isDisabled && isSubmitted) {
    return (
      <div className="t-flex t-flex-col t-gap-1">
        {uploadedFiles.map((file) => (
          <div key={file.uuid} className="t-group t-flex t-items-center">
            <FilePreviewTag
              file={{ ...file, uuid: file.file_uuid }}
              block
              size="small"
              className="!t-border-none"
            />
            <Button
              type="button"
              customType="ghost_icon"
              size="small"
              className="t-invisible group-hover:t-visible"
              onClick={() =>
                downloadFile({ file_id: file.file_uuid, group_id: groupId })
              }
            >
              <DownloadIconV2 className="t-text-purple" />
            </Button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div
        className={classNames({
          "t-cursor-not-allowed t-opacity-60": isDisabled,
        })}
      >
        <FileInput
          withInkleDocs
          groupId={groupId}
          onDrop={handleDrop}
          onDelete={handleDelete}
          onFileClick={({ uuid }) => {
            const file = uploadedFiles.find((f) => f.uuid === uuid);
            setPreviewFileUuid(file?.file_uuid ?? null);
          }}
          isDeleting={!!deletingUuid}
          fileDeletingUuid={deletingUuid ?? undefined}
          disabled={isDisabled || isUploading}
          multiple={allowMultiple}
          files={isDisabled && !isSubmitted ? [] : uploadedFiles}
          file={uploadedFiles[0]}
          isUploading={isUploading}
        />
      </div>
      <Preview
        showModal={!!previewFileUuid}
        closeModal={() => setPreviewFileUuid(null)}
        previewId={previewFileUuid}
      />
    </>
  );
};
